if (!customElements.get("mini-cart-complementary-upsell")) {
  const STORAGE_KEY = "mini-cart:last-added-product-id";
  const ALLOWED_INTENTS = new Set(["complementary", "related"]);

  const getStoredLatestProductId = () => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const setStoredLatestProductId = (productId) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(productId));
    } catch (error) {
      // Ignore storage errors so the upsell can still run.
    }
  };

  const getProductIdFromItem = (item) => {
    if (!item || typeof item !== "object") {
      return null;
    }

    const productId = item.product_id || item.productId || item.product?.id || null;

    if (!productId) {
      return null;
    }

    return String(productId);
  };

  class MiniCartComplementaryUpsell extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.latestAddedProductId = getStoredLatestProductId();
      this.handleCartItemAdd = this.handleCartItemAdd.bind(this);
      this.handleMiniCartOpened = this.handleMiniCartOpened.bind(this);
    }

    connectedCallback() {
      document.addEventListener("cart:item-add", this.handleCartItemAdd);
      document.addEventListener("mini-cart:opened", this.handleMiniCartOpened);
      this.loadRecommendations();
    }

    disconnectedCallback() {
      document.removeEventListener("cart:item-add", this.handleCartItemAdd);
      document.removeEventListener("mini-cart:opened", this.handleMiniCartOpened);
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    handleCartItemAdd(event) {
      const items = Array.isArray(event?.detail?.items) ? event.detail.items : [];
      const productId = getProductIdFromItem(items[0]);

      if (!productId) {
        return;
      }

      this.latestAddedProductId = productId;
      setStoredLatestProductId(productId);
      this.loadRecommendations();
    }

    getCartProductIds() {
      const productIds = (this.dataset.cartProductIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const seen = new Set();
      return productIds.filter((id) => {
        if (seen.has(id)) {
          return false;
        }

        seen.add(id);
        return true;
      });
    }

    getCandidateProductIds() {
      const cartProductIds = this.getCartProductIds();

      if (cartProductIds.length === 0) {
        return [];
      }

      const candidates = [];
      const candidateSet = new Set();

      if (this.latestAddedProductId && cartProductIds.includes(this.latestAddedProductId)) {
        candidates.push(this.latestAddedProductId);
        candidateSet.add(this.latestAddedProductId);
      }

      cartProductIds.forEach((id) => {
        if (!candidateSet.has(id)) {
          candidates.push(id);
          candidateSet.add(id);
        }
      });

      return candidates;
    }

    buildRecommendationsUrl(productId) {
      const baseUrl = window.routes?.product_recommendations_url;
      if (!baseUrl) {
        return null;
      }

      const selectedIntent = (this.dataset.intent || "complementary").trim().toLowerCase();
      const intent = ALLOWED_INTENTS.has(selectedIntent) ? selectedIntent : "complementary";

      const params = new URLSearchParams({
        section_id: "mini-cart-complementary-products",
        product_id: productId,
        intent,
        limit: "10",
      });

      return `${baseUrl}?${params.toString()}`;
    }

    async getRecommendationsMarkup(productId) {
      const requestUrl = this.buildRecommendationsUrl(productId);
      if (!requestUrl) {
        return "";
      }

      const response = await fetch(requestUrl, {
        signal: this.abortController.signal,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        return "";
      }

      const text = await response.text();
      const html = document.createElement("div");
      html.innerHTML = text;

      const recommendations = html.querySelector("product-recommendations");
      if (!recommendations || !recommendations.innerHTML.trim()) {
        return "";
      }

      if (!recommendations.querySelector(".grid-item")) {
        return "";
      }

      return recommendations.innerHTML.trim();
    }

    hideUpsell() {
      this.classList.add("mini-cart-complementary-upsell-hidden");
      this.innerHTML = "";
    }

    resizeCarousel() {
      const carousel = this.querySelector("carousel-slider");
      if (!carousel?.flickity) {
        return false;
      }

      carousel.flickity.resize();
      return true;
    }

    resizeCarouselWithRetry() {
      const run = () => {
        if (this.resizeCarousel()) {
          return;
        }

        setTimeout(() => {
          this.resizeCarousel();
        }, 250);
      };

      requestAnimationFrame(run);
    }

    handleMiniCartOpened() {
      this.resizeCarouselWithRetry();
    }

    renderUpsell(recommendationsMarkup) {
      const heading = this.dataset.heading || "Frequently bought together";
      const wrapper = document.createElement("div");
      wrapper.className = "mini-cart-complementary-upsell";

      const headingElement = document.createElement("p");
      headingElement.className = "mini-cart-complementary-upsell-heading h5";
      headingElement.textContent = heading;

      const content = document.createElement("div");
      content.className = "mini-cart-complementary-upsell-content";
      content.innerHTML = recommendationsMarkup;

      wrapper.appendChild(headingElement);
      wrapper.appendChild(content);
      this.replaceChildren(wrapper);
      this.classList.remove("mini-cart-complementary-upsell-hidden");
      this.resizeCarouselWithRetry();
    }

    async loadRecommendations() {
      const candidateProductIds = this.getCandidateProductIds();
      if (candidateProductIds.length === 0) {
        this.hideUpsell();
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      try {
        for (const productId of candidateProductIds) {
          const recommendationsMarkup = await this.getRecommendationsMarkup(productId);
          if (recommendationsMarkup) {
            this.renderUpsell(recommendationsMarkup);
            return;
          }
        }

        this.hideUpsell();
      } catch (error) {
        if (error?.name !== "AbortError") {
          this.hideUpsell();
        }
      }
    }
  }

  customElements.define("mini-cart-complementary-upsell", MiniCartComplementaryUpsell);
}
